import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useAuth } from '../../contexts/AuthContext';
import { validateLoginForm, validateInitialPassword, setInitialPassword } from './services';

export interface LoginFormState {
  username: string;
  password: string;
}

export function useLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState<LoginFormState>({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Initial password modal state
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  const handleChange = useCallback((field: keyof LoginFormState, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    const validationError = validateLoginForm(form.username, form.password);
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    setError('');
    try {
      const result = await login(form.username, form.password, rememberMe);
      if (result?.mustChangePassword) {
        setShowPwdModal(true);
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || '登入失敗');
    } finally {
      setLoading(false);
    }
  }, [form, rememberMe, login, navigate]);

  const handleSetPassword = useCallback(async () => {
    const validationError = validateInitialPassword(newPwd, confirmPwd);
    if (validationError) { message.error(validationError); return; }

    setPwdLoading(true);
    try {
      await setInitialPassword(newPwd);
      message.success('密碼設定成功');
      setShowPwdModal(false);
      navigate('/');
    } catch {
      message.error('密碼設定失敗，請重試');
    } finally {
      setPwdLoading(false);
    }
  }, [newPwd, confirmPwd, navigate]);

  return {
    form, loading, error, rememberMe,
    showPwdModal, newPwd, confirmPwd, pwdLoading,
    handleChange, handleSubmit, handleSetPassword,
    setRememberMe, setNewPwd, setConfirmPwd,
  };
}
